import React from 'react';
import {Input} from 'formsy-react-components';
import PropTypes from 'prop-types';
import {cleanProps} from 'formsy-react-components/release/prop-utilities';
import Icon from 'formsy-react-components/release/icon';
import Row from 'formsy-react-components/release/row';
import SafeHTML from './SafeHTML.react';
import {defineMessages, IntlProvider, IntlShape} from 'react-intl';
import {getLocale} from '../../browser/locales';
import {getMessages} from '../../browser/getMessages';
import { DELETE_FIELD_EXPRESSION } from '../../common/model-api/ChannelType';

// eslint-disable-next-line
var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

const messages = defineMessages({
    delete: {id: 'passwordInput.delete', 'defaultMessage': 'This field will be deleted after saving the configuration.'}
});

const {intl} = new IntlProvider({locale: getLocale(), messages: getMessages()}, {}).getChildContext();

interface PasswordInputProps {
    addonAfter?: string | React.ReactNode;
    addonBefore?: string | React.ReactNode;
    buttonAfter?: React.ReactNode;
    buttonBefore?: React.ReactNode;
    htmlHelp?: string;
    type?: "color" | "date" | "datetime" | "datetime-local" | "email" | "hidden" | "month" | "number" | "password" | "range" | "search" | "tel" | "text" | "time" | "url" | "week";
}

export default class PasswordInput extends Input<PasswordInputProps> {
	static propTypes = {
		addonAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		addonBefore: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        buttonAfter: PropTypes.node,
		buttonBefore: PropTypes.node,
		htmlHelp: PropTypes.string,
        type: PropTypes.oneOf(['color', 'date', 'datetime', 'datetime-local', 'email', 'hidden', 'month', 'number', 'password', 'range', 'search', 'tel', 'text', 'time', 'url', 'week']),
    };

	renderHelp = function() {
		if (this.props.htmlHelp) {
			return (
                <span className="help-block"><SafeHTML value={this.props.htmlHelp + '<br/>'} /></span>
			);
		}

		if (this.props.help) {
			return (
				<span className="help-block">{this.props.help}</span>
			);
		}

		return null;
	};

    renderElement = function() {
        let self = this;

        let className = 'form-control';
        if (['range'].indexOf(this.props.type) !== -1) {
            className = null;
        }

		let propsCopy = JSON.parse(JSON.stringify(this.props));

		delete propsCopy.htmlHelp;

        return React.createElement('input', _extends({
            ref: function ref(c) {
				self.element = c;
                return self.element;
            },
            className: className
        }, cleanProps(propsCopy), {
            id: this.getId(),
            value: this.getValue(),
            onChange: this.changeValue,
            disabled: this.isFormDisabled() || this.props.disabled,
            placeholder: this.props.placeholder
        }));
    };

    renderDeletedElement = function() {
        let self = this;

        let className = 'form-control';
        if (['range'].indexOf(this.props.type) !== -1) {
            className = null;
        }

		let propsCopy = JSON.parse(JSON.stringify(this.props));

		delete propsCopy.htmlHelp;

        return React.createElement('input', _extends({
            ref: function ref(c) {
				self.element = c;
                return self.element;
            },
            className: className,
            type: 'text'
        }, cleanProps(propsCopy), {
            id: this.getId() + '_deleted',
            disabled: true,
            placeholder: intl.formatMessage(messages.delete)
        }));
    };

	render() {
        let element = this.renderElement();
        let deletedElement = this.renderDeletedElement();

        if (this.props.type === 'hidden') {
            return element;
        }

        if (this.props.addonBefore || this.props.addonAfter || this.props.buttonBefore || this.props.buttonAfter) {
            element = this.renderInputGroup(element);
        }

        if (this.getLayout() === 'elementOnly') {
            return element;
        }

        let warningIcon = null;
        if (this.showErrors()) {
            warningIcon = React.createElement(Icon, { symbol: 'remove', className: 'form-control-feedback' });
        }

        return React.createElement(
            Row,
            _extends({}, this.getRowProperties(), {
                htmlFor: this.getId()
            }),
            <div className="input-group">
                {this.state.shouldDelete ? deletedElement : element}
                <span className="input-group-btn">
                    <button className="btn btn-default" onClick={() => {
                        this.setValue(DELETE_FIELD_EXPRESSION);
                        this.setState({shouldDelete: true});
                    }} type="button">
                        <Icon symbol="remove" />
                    </button>
                </span>
            </div>,
            warningIcon,
            this.renderHelp(),
            this.renderErrorMessage()
        );
    }
}
